import React, { useState, useEffect, useMemo } from 'react';
import { doc, getDoc, getDocs, collection, updateDoc, addDoc, deleteDoc, query, where, limit } from 'firebase/firestore';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { db, auth } from '../firebase';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Sidebar, { SIDEBAR_W } from '../components/Sidebar';
import {
  getPaymentStatus, PAYMENT_STATUS, fetchPaymentsForInvoices,
  syncInvoicePaymentMirror, mirrorSyncFailedMessage,
} from '../utils/paymentStatus';
import { round2 } from '../utils/lineMath';
import { normalizePhones, getPrimaryPhone } from '../utils/customerPhone';
import { propagateCustomerDetails, propagationFailureMessage, customerDocFields } from '../utils/customerPropagation';
import CreditHoldWarning, { creditHoldBy } from '../components/CreditHoldWarning';
import { useData } from '../context/DataContext';
import { useRole } from '../hooks/useRole';

const PAYMENT_TERMS_OPTIONS = ['Due on Receipt', 'Net 15', 'Net 30', 'Net 60'];

// Row labels for the engine's statuses (utils/paymentStatus.js).
const STATUS_LABEL = {
  [PAYMENT_STATUS.UNPAID]: 'UNPAID',
  [PAYMENT_STATUS.PARTIAL]: 'PARTIAL',
  [PAYMENT_STATUS.PAID]: 'PAID',
  [PAYMENT_STATUS.CREDIT]: 'CREDIT',
};

// Cheap existence check: read AT MOST 1 doc per collection (limit(1)), except
// quotes where drafts must be excluded client-side (drafts are never persisted to
// Firestore, so a where('isDraft','==',false) would wrongly exclude every doc).
// Returns true if the customer has any FINALIZED quote, any invoice, or any
// payment. Throws on error so callers can fail safe (BLOCK the delete).
async function customerHasLinkedDocs(customerId) {
  const invSnap = await getDocs(query(collection(db, 'invoices'), where('customerId', '==', customerId), limit(1)));
  if (!invSnap.empty) return true;
  const paySnap = await getDocs(query(collection(db, 'payments'), where('customerId', '==', customerId), limit(1)));
  if (!paySnap.empty) return true;
  const quoteSnap = await getDocs(query(collection(db, 'quotes'), where('customerId', '==', customerId)));
  if (quoteSnap.docs.some(d => !d.data().isDraft)) return true;
  return false;
}

function CustomerProfile({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const userName = user?.email?.split('@')[0]?.toUpperCase();
  // Patch the global customers cache on edits so the Customers list, document
  // pickers, and the SALE/INVOICE account lookup update instantly (not just this
  // page's local `customer` state) — required for the longer cache TTL to be safe.
  const { updateCustomerInCache, removeCustomerFromCache } = useData();
  const { isAdmin, canManageCreditHold } = useRole();

  const [customer, setCustomer] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [payments, setPayments] = useState([]);
  // Payment records for THIS customer's invoices, keyed by invoiceId — the input
  // to every invoice status and to the balance on this page.
  //
  // Read by invoiceId, not taken from `payments` above (which is by customerId):
  // a payment record keeps the customerId it was taken under, so if an invoice
  // is later moved to another customer in the editor, its payments would be
  // missed and a paid invoice would read as owed. Status belongs to the invoice,
  // so it is looked up by invoice.
  const [invoicePayments, setInvoicePayments] = useState({});
  // 'loading' | 'ok' | 'error' — "could not read" must never display as "unpaid".
  const [invoicePaymentsLoad, setInvoicePaymentsLoad] = useState('loading');
  const [drawings, setDrawings] = useState([]);
  const [loading, setLoading] = useState(true);
  // Returning from a document restores the tab it was opened from; anything
  // else starts on Profile as before.
  const [activeTab, setActiveTab] = useState(() => location.state?.returnTab || 'profile');
  const [saving, setSaving] = useState(false);
  // One line under Save: how many documents the last save brought up to date.
  const [saveNote, setSaveNote] = useState('');

  // ── Credit hold (place/lift is limited to two people — see RoleContext) ──
  const [showHoldForm, setShowHoldForm] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [holdBusy, setHoldBusy] = useState(false);

  // `phone` is deliberately NOT in this form any more — it is derived from the
  // phones editor below and written explicitly on save, so there is exactly one
  // source of truth for the number.
  const [profileForm, setProfileForm] = useState({
    name: '', email: '', address: '', notes: '', taxExempt: false,
    defaultPriceLevel: 'A',
  });

  // Multi-phone editor state: [{ number, label, isPrimary }]. Seeded through
  // normalizePhones so a legacy customer (single `phone` string, no array) opens
  // with that number as one primary row — no bulk migration needed.
  const [phones, setPhones] = useState([{ number: '', label: '', isPrimary: true }]);

  const [showConvertForm, setShowConvertForm] = useState(false);
  const [convertForm, setConvertForm] = useState({ creditLimit: '', paymentTerms: 'Net 30' });
  const [converting, setConverting] = useState(false);

  const [editCreditLimit, setEditCreditLimit] = useState(false);
  const [newCreditLimit, setNewCreditLimit] = useState('');
  const [editPaymentTerms, setEditPaymentTerms] = useState(false);
  const [newPaymentTerms, setNewPaymentTerms] = useState('Net 30');

  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: '', date: '', invoiceId: '', notes: '' });
  const [recordingPayment, setRecordingPayment] = useState(false);

  // ── Delete customer (admin + password gated, reuses the app's re-auth flow) ──
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { loadAll(); }, [id]);

  const loadAll = async () => {
    setLoading(true);
    try {
      // Query ONLY this customer's documents instead of scanning whole
      // collections. invoices/quotes/payments all store `customerId` (set at
      // save time), so a where-filter returns the same set the page previously
      // computed client-side — at a fraction of the read cost.
      const [custSnap, invSnap, quotSnap, paySnap, drawSnap] = await Promise.all([
        getDoc(doc(db, 'customers', id)),
        getDocs(query(collection(db, 'invoices'), where('customerId', '==', id))),
        getDocs(query(collection(db, 'quotes'), where('customerId', '==', id))),
        getDocs(query(collection(db, 'payments'), where('customerId', '==', id))),
        // Shop drawings store customerId at save time, same as the documents
        // above. Deliberately NO orderBy here: pairing where() with orderBy()
        // would demand a composite index, and every other query on this page
        // filters server-side then sorts the small result set client-side.
        getDocs(query(collection(db, 'shopDrawings'), where('customerId', '==', id))),
      ]);

      if (custSnap.exists()) {
        const c = { id: custSnap.id, ...custSnap.data() };
        setCustomer(c);
        setProfileForm({ name: c.name || '', email: c.email || '', address: c.address || '', notes: c.notes || '', taxExempt: c.taxExempt || false, defaultPriceLevel: c.defaultPriceLevel || 'A' });
        setPhones(normalizePhones(c));
        setNewCreditLimit(String(c.creditLimit || ''));
        setNewPaymentTerms(c.paymentTerms || 'Net 30');
      }

      // Queries are already scoped to this customer, so no client-side filter is
      // needed — just keep the existing newest-first sort on the small result set.
      const sort = (arr) => arr.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setInvoices(sort(invSnap.docs.map(d => ({ id: d.id, ...d.data() }))));
      setQuotes(sort(quotSnap.docs.map(d => ({ id: d.id, ...d.data() }))));
      setPayments(sort(paySnap.docs.map(d => ({ id: d.id, ...d.data() }))));
      // Shop drawings write createdAt as a Firestore Timestamp (not the ISO
      // string the documents use), so they need their own newest-first sort.
      setDrawings(
        drawSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (tsMillis(b.createdAt) - tsMillis(a.createdAt)))
      );

      // Its own try: if only this read fails, the profile still opens — with
      // statuses and the balance marked unavailable rather than guessed.
      setInvoicePaymentsLoad('loading');
      try {
        setInvoicePayments(await fetchPaymentsForInvoices(invSnap.docs.map(d => d.id)));
        setInvoicePaymentsLoad('ok');
      } catch (err) {
        console.error('Error loading invoice payments:', err);
        setInvoicePayments({});
        setInvoicePaymentsLoad('error');
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      setInvoicePaymentsLoad('error');
    }
    setLoading(false);
  };

  // ── Computed account values ──
  //
  // Every invoice's status, and the balance, come from the shared engine: the
  // invoice's grand total plus its payment records. NOT the stored isPaid /
  // paidAmount on the invoice. Those go stale when an invoice is edited after
  // it was paid — and the balance used to skip any invoice whose stored isPaid
  // said "paid", so a delivery fee added afterwards never reached it.
  const paymentsKnown = invoicePaymentsLoad === 'ok';
  const payStatusById = useMemo(() => {
    const byId = {};
    invoices.forEach(inv => {
      byId[inv.id] = getPaymentStatus(inv.grandTotal, invoicePayments[inv.id] || []);
    });
    return byId;
  }, [invoices, invoicePayments]);
  const billedInvoices = invoices.filter(inv => !inv.isDraft);
  // Money OWED: the sum of balances due. A credit on one invoice is NOT netted
  // off a balance on another. It is shown on its own, because applying an
  // overpayment to a different invoice is the owner's decision, not the app's.
  // null = unknown, because the payment records could not be read.
  const currentBalance = paymentsKnown
    ? round2(billedInvoices.reduce((s, inv) => s + payStatusById[inv.id].balanceDue, 0))
    : null;
  const creditHeld = paymentsKnown
    ? round2(billedInvoices.reduce((s, inv) => s + payStatusById[inv.id].creditAmount, 0))
    : null;
  const creditLimit = parseFloat(customer?.creditLimit || 0);
  const availableCredit = currentBalance === null ? null : creditLimit - currentBalance;

  // ── Helpers ──
  const getDueDate = (invoice) => {
    const terms = customer?.paymentTerms || 'Due on Receipt';
    const d = new Date(invoice.createdAt);
    if (terms === 'Net 15') d.setDate(d.getDate() + 15);
    else if (terms === 'Net 30') d.setDate(d.getDate() + 30);
    else if (terms === 'Net 60') d.setDate(d.getDate() + 60);
    return d.toLocaleDateString('en-US');
  };

  // From the engine. '—' while the payment records are unknown: an invoice
  // whose payments could not be read is not an unpaid one.
  const getStatus = (inv) => {
    if (inv.isDraft) return 'DRAFT';
    if (!paymentsKnown) return '—';
    return STATUS_LABEL[payStatusById[inv.id]?.status] || '—';
  };

  const statusBadgeStyle = (status) => {
    const map = {
      PAID: { backgroundColor: '#E6F7EE', color: '#0F6E56' },
      CREDIT: { backgroundColor: '#E6F1FB', color: '#0077B6' },
      UNPAID: { backgroundColor: '#FBECEC', color: '#A32D2D' },
      PARTIAL: { backgroundColor: '#FDF1DD', color: '#854F0B' },
      DRAFT: { backgroundColor: '#CDE7F5', color: '#6B7C93' },
    };
    return map[status] || { backgroundColor: '#CDE7F5', color: '#6B7C93' };
  };
  const statusBadgeClass = 'px-2 py-0.5 rounded text-xs font-bold';

  const fmt = (n) => `$${parseFloat(n || 0).toFixed(2)}`;
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US') : '—';
  // Shop drawings store createdAt as a Firestore Timestamp rather than the ISO
  // string documents use, so they get their own reader/formatter. Returns 0 for
  // a pending write (server value not resolved yet) so sorting stays stable.
  const tsMillis = (ts) => (ts && typeof ts.toDate === 'function' ? ts.toDate().getTime() : 0);
  const fmtTsDate = (ts) => (tsMillis(ts) ? ts.toDate().toLocaleDateString('en-US') : '—');

  // ── Phone editor handlers ──
  // All immutable. Between them they hold the invariant "exactly one isPrimary"
  // for any list of one or more rows: setPrimary rebuilds every flag, removePhone
  // re-marks the first row when the primary is the one being removed, and
  // addPhone only marks the new row primary when it is the only row.
  const addPhone = () => setPhones(prev => (
    prev.length === 0
      ? [{ number: '', label: '', isPrimary: true }]
      : [...prev, { number: '', label: '', isPrimary: false }]
  ));

  const removePhone = (index) => setPhones(prev => {
    if (prev.length <= 1) return prev; // always keep at least one row
    const next = prev.filter((_, i) => i !== index);
    // Removing the primary leaves none — promote the first survivor.
    if (!next.some(p => p.isPrimary)) next[0] = { ...next[0], isPrimary: true };
    return next;
  });

  const updatePhone = (index, field, value) => setPhones(prev =>
    prev.map((p, i) => (i === index ? { ...p, [field]: value } : p))
  );

  const setPrimary = (index) => setPhones(prev =>
    prev.map((p, i) => ({ ...p, isPrimary: i === index }))
  );

  // Save-time cleanup: trim, drop blank-number rows (an empty row the user never
  // filled in must not persist), and guarantee exactly one primary among whatever
  // survives. Returns [] when nothing is filled in, which is a valid "no phone".
  const cleanPhones = (list) => {
    const kept = list
      .map(p => ({
        number: (p.number || '').trim(),
        label: (p.label || '').trim(),
        isPrimary: !!p.isPrimary,
      }))
      .filter(p => p.number);
    if (kept.length === 0) return [];
    let primarySeen = false;
    kept.forEach(p => {
      if (p.isPrimary && !primarySeen) primarySeen = true;
      else p.isPrimary = false; // collapse any duplicate primaries
    });
    if (!primarySeen) kept[0].isPrimary = true; // primary row was blank and got dropped
    return kept;
  };

  // ── Actions ──
  const handleSaveProfile = async () => {
    setSaving(true);
    setSaveNote('');
    let saved = null;
    try {
      const cleanedPhones = cleanPhones(phones);
      // Write BOTH shapes: the new array, and the legacy single string kept in
      // sync with the primary number. Every existing read site (documents,
      // customer list, search, pickers) still reads the legacy string, so they
      // keep working unchanged until later steps migrate them.
      const payload = {
        ...profileForm,
        phones: cleanedPhones,
        phone: getPrimaryPhone({ phones: cleanedPhones }),
      };
      await updateDoc(doc(db, 'customers', id), payload);
      setCustomer(prev => ({ ...prev, ...payload }));
      updateCustomerInCache(id, payload);
      // Re-seed the editor from what was actually saved, so dropped blank rows
      // disappear and the promoted primary is reflected immediately.
      setPhones(normalizePhones({ phones: cleanedPhones }));
      saved = { ...customer, ...payload };
    } catch (err) {
      console.error(err);
      alert('Error saving. Please try again.');
    }

    // The customer is saved. Now copy the details onto their documents — after,
    // and separately, so a failure here can never undo or block that save.
    if (saved) {
      const result = await propagateCustomerDetails(id, saved);
      const failure = propagationFailureMessage(result);
      if (failure) {
        alert(failure);
      } else {
        // Every document now matches, so the lists on this page can too.
        const fields = customerDocFields(saved);
        setInvoices(prev => prev.map(d => ({ ...d, ...fields })));
        setQuotes(prev => prev.map(d => ({ ...d, ...fields })));
        setDrawings(prev => prev.map(d => ({ ...d, customerName: fields.customerName })));
        if (result.updated > 0) {
          setSaveNote(`Saved. Updated ${result.updated} document${result.updated === 1 ? '' : 's'} with the new details.`);
        }
      }
    }
    setSaving(false);
  };

  const handleConvert = async () => {
    if (!convertForm.creditLimit) { alert('Please enter a credit limit.'); return; }
    setConverting(true);
    try {
      const update = {
        isAccountCustomer: true,
        creditLimit: parseFloat(convertForm.creditLimit),
        paymentTerms: convertForm.paymentTerms,
        accountOpenedDate: new Date().toISOString(),
        currentBalance: 0,
      };
      await updateDoc(doc(db, 'customers', id), update);
      setCustomer(prev => ({ ...prev, ...update }));
      updateCustomerInCache(id, update);
      setNewCreditLimit(String(convertForm.creditLimit));
      setNewPaymentTerms(convertForm.paymentTerms);
      setShowConvertForm(false);
    } catch (err) {
      console.error(err);
      alert('Error converting. Please try again.');
    }
    setConverting(false);
  };

  const handleUpdateCreditLimit = async () => {
    const val = parseFloat(newCreditLimit);
    if (isNaN(val)) return;
    try {
      await updateDoc(doc(db, 'customers', id), { creditLimit: val });
      setCustomer(prev => ({ ...prev, creditLimit: val }));
      updateCustomerInCache(id, { creditLimit: val });
      setEditCreditLimit(false);
    } catch (err) { console.error(err); }
  };

  const handleUpdatePaymentTerms = async () => {
    try {
      await updateDoc(doc(db, 'customers', id), { paymentTerms: newPaymentTerms });
      setCustomer(prev => ({ ...prev, paymentTerms: newPaymentTerms }));
      updateCustomerInCache(id, { paymentTerms: newPaymentTerms });
      setEditPaymentTerms(false);
    } catch (err) { console.error(err); }
  };

  // ── Credit hold ──
  //
  // Only sales@ and jordan@ reach these: the buttons are rendered behind
  // canManageCreditHold, and the guard is repeated here so a stale render
  // cannot write. It is still a UI-level restriction only — Firestore rules
  // allow any authenticated session to write these fields (see teamMembers.js).
  //
  // A hold REQUIRES a written reason: everyone else in the app sees only what
  // is stored, and "on hold, no reason given" tells the salesperson in front of
  // the customer nothing they can act on.
  const handlePlaceCreditHold = async () => {
    const reason = holdReason.trim();
    if (!canManageCreditHold || !reason) return;
    setHoldBusy(true);
    try {
      const fields = {
        creditHold: true,
        creditHoldReason: reason,
        creditHoldBy: userName || '',
        creditHoldAt: new Date().toISOString(),
      };
      await updateDoc(doc(db, 'customers', id), fields);
      setCustomer(prev => ({ ...prev, ...fields }));
      updateCustomerInCache(id, fields);
      setShowHoldForm(false);
      setHoldReason('');
    } catch (err) {
      console.error('Could not place the credit hold:', err);
      alert('Could not place the credit hold. Please try again.');
    }
    setHoldBusy(false);
  };

  // Lifting clears all four fields together. A cleared flag that still names
  // who placed it, or keeps the old reason, reads as a hold that is somehow
  // half-on — and the reason would resurface on the next hold.
  const handleLiftCreditHold = async () => {
    if (!canManageCreditHold) return;
    if (!window.confirm(`Lift the credit hold on ${customer.name}? They will be able to use credit again.`)) return;
    setHoldBusy(true);
    try {
      const fields = { creditHold: false, creditHoldReason: '', creditHoldBy: '', creditHoldAt: null };
      await updateDoc(doc(db, 'customers', id), fields);
      setCustomer(prev => ({ ...prev, ...fields }));
      updateCustomerInCache(id, fields);
    } catch (err) {
      console.error('Could not lift the credit hold:', err);
      alert('Could not lift the credit hold. Please try again.');
    }
    setHoldBusy(false);
  };

  const handleMarkAsPaid = async (invoice) => {
    // Pays what is OWED, computed from the payment records — for an unpaid
    // invoice, its whole total. Never acts while those records are unknown.
    const owed = payStatusById[invoice.id]?.balanceDue || 0;
    if (!paymentsKnown || owed <= 0) return;
    if (!window.confirm(`Mark Invoice ${invoice.docNumber} as fully paid (${fmt(owed)})?`)) return;
    let recorded = false;
    try {
      // The payment record FIRST, the status second. This used to write
      // isPaid: true before the payment existed, so a failure in between left
      // an invoice marked paid with no money behind it.
      await addDoc(collection(db, 'payments'), {
        customerId: id, customerName: customer.name,
        amount: owed,
        date: new Date().toISOString(),
        invoiceId: invoice.id, invoiceNumber: invoice.docNumber,
        notes: 'Marked as paid', recordedBy: userName,
        createdAt: new Date().toISOString(),
      });
      recorded = true;
      // Mirror rebuilt from the payments collection — see utils/paymentStatus.js.
      await syncInvoicePaymentMirror(invoice.id, { paidAt: new Date().toISOString() });
    } catch (err) {
      console.error(err);
      alert(recorded
        ? mirrorSyncFailedMessage(`The payment of ${fmt(owed)} WAS recorded. Do not enter it again.`)
        : 'Error. Nothing was recorded. Please try again.');
    }
    if (recorded) loadAll();
  };

  const handleRecordPayment = async () => {
    if (!paymentForm.amount || !paymentForm.invoiceId) { alert('Please enter an amount and select an invoice.'); return; }
    setRecordingPayment(true);
    const amount = parseFloat(paymentForm.amount);
    let recorded = false;
    try {
      const targetInv = invoices.find(inv => inv.id === paymentForm.invoiceId);

      // Recorded exactly as entered. More than the balance is not capped or
      // refused — it shows as a credit on the invoice.
      await addDoc(collection(db, 'payments'), {
        customerId: id, customerName: customer.name, amount,
        date: paymentForm.date || new Date().toISOString(),
        invoiceId: paymentForm.invoiceId,
        invoiceNumber: targetInv?.docNumber || '',
        notes: paymentForm.notes || '', recordedBy: userName,
        createdAt: new Date().toISOString(),
      });
      recorded = true;

      // Rebuilt from the payments COLLECTION and the invoice's current total,
      // not "stored paidAmount + amount", which drifted.
      await syncInvoicePaymentMirror(paymentForm.invoiceId, { paidAt: new Date().toISOString() });

      // customers.currentBalance is deliberately NO LONGER written here. It was
      // "the balance on screen minus this amount" — a stored copy of a derived
      // number, never updated when invoices were created or edited, and read
      // by nothing in the app (the balance shown is computed above).
    } catch (err) {
      console.error(err);
      alert(recorded
        ? mirrorSyncFailedMessage(`The payment of ${fmt(amount)} WAS recorded. Do not enter it again.`)
        : 'Error recording payment. Nothing was recorded. Please try again.');
    }
    if (recorded) {
      setShowPaymentForm(false);
      setPaymentForm({ amount: '', date: '', invoiceId: '', notes: '' });
      loadAll();
    }
    setRecordingPayment(false);
  };

  const initiateDelete = () => {
    if (!isAdmin) { alert('Only admins can delete customers.'); return; }
    setDeletePassword('');
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletePassword('');
    setDeleteError('');
  };

  const confirmDelete = async () => {
    if (!customer) return;
    setDeleting(true);
    setDeleteError('');
    try {
      // 1. Password gate — reuse the app's re-authentication (own login password).
      const credential = EmailAuthProvider.credential(auth.currentUser.email, deletePassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
    } catch (err) {
      console.error('Reauth error:', err);
      setDeleteError(
        err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential'
          ? '❌ Incorrect password. Please try again.'
          : '❌ Error: ' + err.message
      );
      setDeleting(false);
      return;
    }

    // 2. Existence check — FAIL SAFE: any error blocks the delete.
    let hasLinks;
    try {
      hasLinks = await customerHasLinkedDocs(id);
    } catch (err) {
      console.error('Existence check failed:', err);
      setDeleteError('❌ Could not verify customer history. Deletion blocked for safety.');
      setDeleting(false);
      return;
    }
    if (hasLinks) {
      setDeleteError('Cannot delete: this customer has linked invoices, quotes, or payments.');
      setDeleting(false);
      return;
    }

    // 3. Real delete + instant cache removal, then back to the list.
    try {
      await deleteDoc(doc(db, 'customers', id));
      removeCustomerFromCache(id);
      navigate('/customers');
    } catch (err) {
      console.error('Delete error:', err);
      setDeleteError('❌ Error: ' + err.message);
      setDeleting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#CDE7F5', fontFamily: 'Manrope, sans-serif' }}>
      <p className="text-xl" style={{ color: '#6B7C93' }}>Loading customer profile...</p>
    </div>
  );

  if (!customer) return (
    <>
      <Sidebar />
      <div className="min-h-screen flex items-center justify-center" style={{ marginLeft: SIDEBAR_W, backgroundColor: '#CDE7F5', fontFamily: 'Manrope, sans-serif' }}>
        <p className="text-xl" style={{ color: '#A32D2D' }}>Customer not found.</p>
      </div>
    </>
  );

  const tabs = [
    { key: 'profile', label: 'Profile' },
    { key: 'account', label: 'Account', hidden: !customer.isAccountCustomer },
    // History was one table mixing both. Two lists, because the questions are
    // different ones: "what have we quoted them" and "what have we billed them"
    // are asked separately and answered separately.
    { key: 'quotes', label: 'Quotes' },
    { key: 'invoices', label: 'Invoices' },
    { key: 'drawings', label: 'Drawings' },
    { key: 'payments', label: 'Payments', hidden: !customer.isAccountCustomer },
  ].filter(t => !t.hidden);

  // Invoices with money still owed, computed. Empty while the payment records
  // are unknown, rather than a list that might include paid ones.
  const unpaidInvoices = paymentsKnown
    ? invoices.filter(inv => !inv.isDraft && payStatusById[inv.id].balanceDue > 0)
    : [];
  // Newest first, the same order the combined table used — split, not re-sorted,
  // so a row sits where the customer expects to find it.
  const byNewest = (a, b) => new Date(b.createdAt) - new Date(a.createdAt);
  const quoteItems = quotes.map(d => ({ ...d, _type: 'quote' })).sort(byNewest);
  const invoiceItems = invoices.map(d => ({ ...d, _type: 'invoice' })).sort(byNewest);

  // ── OPENING A DOCUMENT FROM ONE OF THE LISTS ──
  //
  // The ordered ids of the list, and where in it this row sits, travel with the
  // navigation — the same mechanism ShopDrawings uses to open one drawing. The
  // document page reads it and offers next/previous WITHIN that list; a document
  // opened any other way carries no state and gets no controls.
  //
  // `returnTab` comes back with them so Back lands on the tab they left from
  // rather than dumping them on Profile.
  //
  // ⚠️ This goes to the VIEWER, not the editor. It used to open
  // /edit-document. See the note in the report — stepping through a customer's
  // documents is a reading task, and the editor has unsaved-changes state that
  // has no business being carried between documents by a next button.
  const openFromList = (item, ids, index) => {
    navigate(`/view-document/${item._type}/${item.id}`, {
      state: {
        docNav: {
          ids,
          index,
          type: item._type,
          returnTo: `/customer/${id}`,
          returnTab: activeTab,
        },
      },
    });
  };

  const totalQuoted = quotes.reduce((s, q) => s + parseFloat(q.grandTotal || 0), 0);
  const totalInvoiced = invoices.filter(i => !i.isDraft).reduce((s, i) => s + parseFloat(i.grandTotal || 0), 0);
  const totalPaid = payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);

  return (
    <>
      <Sidebar />
      <div className="min-h-screen" style={{ marginLeft: SIDEBAR_W, backgroundColor: '#CDE7F5', fontFamily: 'Manrope, sans-serif' }}>

      <div className="p-6 max-w-5xl mx-auto">

        {/* Slim header row */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <h1 className="text-xl font-bold" style={{ color: '#03045E' }}>Customer Profile</h1>
          <button
            onClick={() => navigate('/customers')}
            className="text-sm font-semibold px-4 py-2 rounded-lg border transition"
            style={{ backgroundColor: '#E6F1FB', color: '#0077B6', borderColor: '#E3ECF5' }}
          >
            ← Customers
          </button>
        </div>


        {/* Credit hold — above the header card, the first thing on the page.
            Visible to everyone; only the two permitted people get the controls
            below it. */}
        <CreditHoldWarning customer={customer} className="mb-2" />
        {customer.creditHold && (
          <p className="text-xs mb-4" style={{ color: '#6B7C93' }}>{creditHoldBy(customer)}</p>
        )}

        {/* Place / lift — ONLY for the two permitted accounts. Everyone else
            sees the warning above and the line naming who placed it, and has
            nothing to press. */}
        {canManageCreditHold && (
          <div className="mb-4">
            {customer.creditHold ? (
              <button onClick={handleLiftCreditHold} disabled={holdBusy}
                className="disabled:opacity-60 font-bold px-4 py-2 rounded-lg text-sm"
                style={{ backgroundColor: '#FFFFFF', color: '#0F6E56', border: '1px solid #0F6E56' }}>
                {holdBusy ? 'Working…' : '✅ Lift Credit Hold'}
              </button>
            ) : showHoldForm ? (
              <div className="rounded-xl p-4" style={{ backgroundColor: '#FFFFFF', border: '2px solid #A32D2D' }}>
                <label className="block font-semibold text-sm mb-1" style={{ color: '#A32D2D' }}>
                  Reason for the credit hold <span className="font-normal">— required, and shown to whoever serves this customer</span>
                </label>
                <textarea rows="2" value={holdReason} onChange={e => setHoldReason(e.target.value)}
                  placeholder="e.g. Account 90 days past due"
                  className="w-full rounded-lg px-3 py-2 focus:outline-none mb-3"
                  style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }} />
                <div className="flex gap-2">
                  <button onClick={handlePlaceCreditHold} disabled={holdBusy || !holdReason.trim()}
                    title={holdReason.trim() ? 'Place the hold' : 'Enter a reason first'}
                    className="disabled:opacity-40 disabled:cursor-not-allowed font-bold px-5 py-2 rounded-lg text-sm"
                    style={{ backgroundColor: '#A32D2D', color: '#FFFFFF' }}>
                    {holdBusy ? 'Saving…' : 'Place Credit Hold'}
                  </button>
                  <button onClick={() => { setShowHoldForm(false); setHoldReason(''); }}
                    className="font-bold px-5 py-2 rounded-lg text-sm"
                    style={{ backgroundColor: '#E6F1FB', color: '#6B7C93', border: '1px solid #E3ECF5' }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowHoldForm(true)}
                className="font-bold px-4 py-2 rounded-lg text-sm"
                style={{ backgroundColor: '#FBECEC', color: '#A32D2D', border: '1px solid #A32D2D' }}>
                ⛔ Place Credit Hold
              </button>
            )}
          </div>
        )}

        {/* Profile Header Card */}
        <div className="rounded-xl p-6 mb-6" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5' }}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-2xl font-bold" style={{ color: '#03045E' }}>{customer.name}</h2>
                {customer.isAccountCustomer ? (
                  <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: '#0077B6', color: '#FFFFFF' }}>ACCOUNT CUSTOMER</span>
                ) : (
                  <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: '#E6F1FB', color: '#6B7C93' }}>REGULAR CUSTOMER</span>
                )}
                {customer.taxExempt && (
                  <span className="px-2 py-1 rounded text-xs font-bold" style={{ backgroundColor: '#E6F7EE', color: '#0F6E56' }}>TAX EXEMPT</span>
                )}
                <CreditHoldWarning customer={customer} size="compact" />
              </div>
              <p className="text-sm" style={{ color: '#6B7C93' }}>Customer # <span className="font-mono font-bold" style={{ color: '#0077B6' }}>{customer.customerNumber}</span></p>
              {customer.isAccountCustomer && (
                <p className="text-xs mt-0.5" style={{ color: '#6B7C93' }}>Account opened: {fmtDate(customer.accountOpenedDate)} · Terms: {customer.paymentTerms}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!customer.isAccountCustomer && (
                <button
                  onClick={() => setShowConvertForm(true)}
                  className="font-bold px-4 py-2 rounded-lg text-sm"
                  style={{ backgroundColor: '#0077B6', color: '#FFFFFF' }}
                >
                  ⭐ Convert to Account Customer
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={initiateDelete}
                  className="font-bold px-4 py-2 rounded-lg text-sm"
                  style={{ backgroundColor: '#FBECEC', color: '#A32D2D', border: '1px solid #A32D2D' }}
                >
                  🗑 Delete Customer
                </button>
              )}
            </div>
          </div>

          {/* Convert form */}
          {showConvertForm && (
            <div className="rounded-xl p-4 mb-4" style={{ backgroundColor: '#E6F1FB', border: '1px solid #E3ECF5' }}>
              <h4 className="font-bold mb-3" style={{ color: '#03045E' }}>Set Up Account</h4>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block font-semibold text-sm mb-1" style={{ color: '#03045E' }}>Credit Limit ($)</label>
                  <input type="number" value={convertForm.creditLimit} onChange={e => setConvertForm(f => ({ ...f, creditLimit: e.target.value }))}
                    className="w-full rounded-lg px-3 py-2 focus:outline-none"
                    style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5' }}
                    onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'} placeholder="0.00" />
                </div>
                <div>
                  <label className="block font-semibold text-sm mb-1" style={{ color: '#03045E' }}>Payment Terms</label>
                  <select value={convertForm.paymentTerms} onChange={e => setConvertForm(f => ({ ...f, paymentTerms: e.target.value }))}
                    className="w-full rounded-lg px-3 py-2 focus:outline-none"
                    style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5' }}
                    onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'}>
                    {PAYMENT_TERMS_OPTIONS.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={handleConvert} disabled={converting}
                  className="disabled:opacity-60 font-bold px-5 py-2 rounded-lg text-sm" style={{ backgroundColor: '#0077B6', color: '#FFFFFF' }}>
                  {converting ? 'Converting...' : 'Confirm'}
                </button>
                <button onClick={() => setShowConvertForm(false)} className="font-bold px-5 py-2 rounded-lg text-sm" style={{ backgroundColor: '#E6F1FB', color: '#6B7C93', border: '1px solid #E3ECF5' }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── TOTALS ── ABOVE the tab strip, deliberately.
            Total Quoted, Total Invoiced and Total Paid describe the CUSTOMER,
            not any one list: the first belongs to Quotes, the second to
            Invoices and the third to Payments. Inside a tab it would be right
            for one of the three and hidden from the others; duplicated it would
            be two copies to keep true. Above them all it is visible wherever
            the user happens to be. */}
        <div className="flex gap-6 mb-4 p-4 rounded-xl text-sm" style={{ backgroundColor: '#CDE7F5', color: '#6B7C93' }}>
          <span>Total Quoted: <span className="font-bold" style={{ color: '#0077B6' }}>{fmt(totalQuoted)}</span></span>
          <span>Total Invoiced: <span className="font-bold" style={{ color: '#03045E' }}>{fmt(totalInvoiced)}</span></span>
          <span>Total Paid: <span className="font-bold" style={{ color: '#0F6E56' }}>{fmt(totalPaid)}</span></span>
        </div>

        {invoicePaymentsLoad === 'error' && (
          <div className="mb-4 p-3 rounded-xl text-sm font-semibold" style={{ backgroundColor: '#FDF1DD', color: '#854F0B', border: '1px solid #E6C998' }}>
            ⚠️ The payment records for this customer&apos;s invoices could not be loaded, so invoice statuses and the balance are not shown. Reload the page to try again.
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 rounded-xl p-1 w-fit" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5' }}>
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className="px-5 py-2 rounded-lg font-semibold text-sm transition"
              style={activeTab === tab.key
                ? { backgroundColor: '#0077B6', color: '#FFFFFF' }
                : { backgroundColor: 'transparent', color: '#6B7C93' }}
              onMouseEnter={e => { if (activeTab !== tab.key) e.currentTarget.style.backgroundColor = '#E6F1FB'; }}
              onMouseLeave={e => { if (activeTab !== tab.key) e.currentTarget.style.backgroundColor = 'transparent'; }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Tab 1: Profile ── */}
        {activeTab === 'profile' && (
          <div className="rounded-xl p-6" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5' }}>
            <h3 className="font-bold text-lg mb-5" style={{ color: '#03045E' }}>Customer Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block font-semibold text-sm mb-1" style={{ color: '#03045E' }}>Full Name</label>
                <input value={profileForm.name} onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full rounded-lg px-3 py-2 focus:outline-none" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                  onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'} />
              </div>
              {/* Phone numbers — one row per number, exactly one marked primary.
                  The primary is the single number printed on quotes/invoices. */}
              <div>
                <label className="block font-semibold text-sm mb-1" style={{ color: '#03045E' }}>
                  Phone Numbers
                  <span className="font-normal ml-1" style={{ color: '#6B7C93' }}>— ✓ marks the one shown on documents</span>
                </label>
                <div className="space-y-2">
                  {phones.map((p, i) => (
                    <div key={i} className="rounded-lg p-2" style={{ backgroundColor: p.isPrimary ? '#E6F1FB' : '#FFFFFF', border: '1px solid #E3ECF5' }}>
                      <input value={p.number} onChange={e => updatePhone(i, 'number', e.target.value)}
                        placeholder="Phone number"
                        className="w-full rounded-lg px-3 py-2 focus:outline-none mb-2" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                        onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'} />
                      <div className="flex items-center gap-2">
                        <input value={p.label} onChange={e => updatePhone(i, 'label', e.target.value)}
                          placeholder="Label (e.g. Office, Cell, name)"
                          className="flex-1 min-w-0 rounded-lg px-3 py-2 focus:outline-none" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                          onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'} />
                        <button type="button" onClick={() => setPrimary(i)}
                          title={p.isPrimary ? 'This is the primary number' : 'Make this the primary number'}
                          className="px-3 py-2 rounded-lg text-sm font-bold transition flex-shrink-0"
                          style={p.isPrimary
                            ? { backgroundColor: '#0077B6', color: '#FFFFFF', border: '1px solid #0077B6' }
                            : { backgroundColor: '#FFFFFF', color: '#6B7C93', border: '1px solid #E3ECF5' }}>
                          ✓
                        </button>
                        <button type="button" onClick={() => removePhone(i)}
                          disabled={phones.length <= 1}
                          title={phones.length <= 1 ? 'At least one phone row is required' : 'Remove this number'}
                          className="px-3 py-2 rounded-lg text-sm font-bold transition flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                          style={{ backgroundColor: '#FBECEC', color: '#A32D2D', border: '1px solid #E9C3C3' }}>
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={addPhone}
                  className="mt-2 px-3 py-2 rounded-lg text-sm font-bold"
                  style={{ backgroundColor: '#FFFFFF', color: '#0077B6', border: '1px solid #E3ECF5' }}>
                  + Add phone number
                </button>
              </div>
              <div>
                <label className="block font-semibold text-sm mb-1" style={{ color: '#03045E' }}>Email</label>
                <input type="email" value={profileForm.email} onChange={e => setProfileForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full rounded-lg px-3 py-2 focus:outline-none" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                  onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'} />
              </div>
              <div>
                <label className="block font-semibold text-sm mb-1" style={{ color: '#03045E' }}>Address</label>
                <input value={profileForm.address} onChange={e => setProfileForm(f => ({ ...f, address: e.target.value }))}
                  className="w-full rounded-lg px-3 py-2 focus:outline-none" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                  onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'} />
              </div>
              <div className="md:col-span-2">
                <label className="block font-semibold text-sm mb-1" style={{ color: '#03045E' }}>Notes</label>
                <textarea rows="3" value={profileForm.notes} onChange={e => setProfileForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full rounded-lg px-3 py-2 focus:outline-none" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                  onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'} />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="taxExemptEdit" checked={profileForm.taxExempt}
                  onChange={e => setProfileForm(f => ({ ...f, taxExempt: e.target.checked }))} className="w-4 h-4" style={{ accentColor: '#0077B6' }} />
                <label htmlFor="taxExemptEdit" className="font-semibold text-sm" style={{ color: '#03045E' }}>Tax Exempt</label>
              </div>
              <div>
                <label className="block font-semibold text-sm mb-1" style={{ color: '#03045E' }}>Default Price Level</label>
                <div className="flex gap-2">
                  {['A', 'B', 'C'].map(level => {
                    const active = profileForm.defaultPriceLevel === level;
                    return (
                      <button key={level} type="button"
                        onClick={() => setProfileForm(f => ({ ...f, defaultPriceLevel: level }))}
                        className="px-4 py-2 rounded-lg text-sm font-bold transition"
                        style={active
                          ? { backgroundColor: '#0077B6', color: '#FFFFFF', border: '1px solid #0077B6' }
                          : { backgroundColor: '#FFFFFF', color: '#0077B6', border: '1px solid #E3ECF5' }}>
                        {level}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <button onClick={handleSaveProfile} disabled={saving}
              className="disabled:opacity-60 font-bold px-6 py-2 rounded-lg" style={{ backgroundColor: '#0077B6', color: '#FFFFFF' }}>
              {saving ? 'Saving...' : '💾 Save Changes'}
            </button>
            {saveNote && <p className="text-sm mt-2" style={{ color: '#0F6E56' }}>{saveNote}</p>}
          </div>
        )}

        {/* ── Tab 2: Account ── */}
        {activeTab === 'account' && customer.isAccountCustomer && (
          <div className="space-y-6">
            {/* Summary cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Credit Limit */}
              <div className="rounded-xl p-4" style={{ backgroundColor: '#CDE7F5', border: '1px solid #E3ECF5' }}>
                <p className="text-xs uppercase font-semibold mb-1" style={{ color: '#6B7C93' }}>💰 Credit Limit</p>
                {editCreditLimit ? (
                  <div className="flex gap-2 items-center mt-1">
                    <input type="number" value={newCreditLimit} onChange={e => setNewCreditLimit(e.target.value)}
                      className="w-24 rounded px-2 py-1 text-sm focus:outline-none" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                      onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'} />
                    <button onClick={handleUpdateCreditLimit} className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#0077B6', color: '#FFFFFF' }}>Save</button>
                    <button onClick={() => setEditCreditLimit(false)} className="text-xs" style={{ color: '#6B7C93' }}>✕</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-2xl font-bold" style={{ color: '#03045E' }}>{fmt(customer.creditLimit)}</p>
                    <button onClick={() => setEditCreditLimit(true)} className="text-sm" style={{ color: '#0077B6' }}>✏️</button>
                  </div>
                )}
              </div>
              {/* Current Balance */}
              <div className="rounded-xl p-4" style={{ backgroundColor: '#CDE7F5', border: '1px solid #E3ECF5' }}>
                <p className="text-xs uppercase font-semibold mb-1" style={{ color: '#6B7C93' }}>🔴 Current Balance</p>
                <p className="text-2xl font-bold" style={{ color: currentBalance > 0 ? '#A32D2D' : '#03045E' }}>{currentBalance === null ? '—' : fmt(currentBalance)}</p>
                {/* Overpayments, on their own line and NOT subtracted from the
                    balance above — see the note on currentBalance. */}
                {creditHeld > 0 && (
                  <p className="text-xs font-bold mt-1" style={{ color: '#0077B6' }}>💙 Credit held: {fmt(creditHeld)} (overpaid)</p>
                )}
              </div>
              {/* Available Credit */}
              <div className="rounded-xl p-4" style={{ backgroundColor: '#CDE7F5', border: '1px solid #E3ECF5' }}>
                <p className="text-xs uppercase font-semibold mb-1" style={{ color: '#6B7C93' }}>✅ Available Credit</p>
                <p className="text-2xl font-bold" style={{ color: availableCredit !== null && availableCredit < 0 ? '#A32D2D' : '#0F6E56' }}>{availableCredit === null ? '—' : fmt(availableCredit)}</p>
              </div>
              {/* Payment Terms */}
              <div className="rounded-xl p-4" style={{ backgroundColor: '#CDE7F5', border: '1px solid #E3ECF5' }}>
                <p className="text-xs uppercase font-semibold mb-1" style={{ color: '#6B7C93' }}>📅 Payment Terms</p>
                {editPaymentTerms ? (
                  <div className="flex gap-2 items-center mt-1">
                    <select value={newPaymentTerms} onChange={e => setNewPaymentTerms(e.target.value)}
                      className="rounded px-2 py-1 text-sm focus:outline-none" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                      onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'}>
                      {PAYMENT_TERMS_OPTIONS.map(t => <option key={t}>{t}</option>)}
                    </select>
                    <button onClick={handleUpdatePaymentTerms} className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#0077B6', color: '#FFFFFF' }}>Save</button>
                    <button onClick={() => setEditPaymentTerms(false)} className="text-xs" style={{ color: '#6B7C93' }}>✕</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold mt-1" style={{ color: '#03045E' }}>{customer.paymentTerms}</p>
                    <button onClick={() => setEditPaymentTerms(true)} className="text-sm" style={{ color: '#0077B6' }}>✏️</button>
                  </div>
                )}
              </div>
            </div>

            {/* Invoices table */}
            <div className="rounded-xl p-5" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5' }}>
              <h3 className="font-bold mb-4" style={{ color: '#03045E' }}>Invoices</h3>
              {invoices.filter(i => !i.isDraft).length === 0 ? (
                <p className="text-sm" style={{ color: '#6B7C93' }}>No invoices found for this customer.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead style={{ backgroundColor: '#E6F1FB' }}>
                    <tr style={{ color: '#03045E' }}>
                      <th className="px-3 py-2 text-left">Invoice #</th>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Due Date</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2 text-right">Paid</th>
                      <th className="px-3 py-2 text-center">Status</th>
                      <th className="px-3 py-2 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.filter(i => !i.isDraft).map((inv, idx) => {
                      const status = getStatus(inv);
                      const pay = payStatusById[inv.id];
                      return (
                        <tr key={inv.id} style={{ backgroundColor: idx % 2 === 0 ? '#FFFFFF' : '#CDE7F5' }}>
                          <td className="px-3 py-2 font-mono font-bold text-xs" style={{ color: '#0077B6' }}>{inv.docNumber}</td>
                          <td className="px-3 py-2 text-xs" style={{ color: '#6B7C93' }}>{fmtDate(inv.createdAt)}</td>
                          <td className="px-3 py-2 text-xs" style={{ color: '#6B7C93' }}>{getDueDate(inv)}</td>
                          <td className="px-3 py-2 text-right font-bold" style={{ color: '#03045E' }}>{fmt(inv.grandTotal)}</td>
                          <td className="px-3 py-2 text-right text-xs" style={{ color: '#6B7C93' }}>{paymentsKnown ? fmt(pay.totalPaid) : '—'}</td>
                          <td className="px-3 py-2 text-center"><span className={statusBadgeClass} style={statusBadgeStyle(status)}>{status}</span></td>
                          <td className="px-3 py-2">
                            <div className="flex gap-1">
                              <button onClick={() => navigate(`/edit-document/invoice/${inv.id}`)}
                                className="font-bold px-2 py-1 rounded text-xs" style={{ backgroundColor: '#E6F1FB', color: '#0077B6' }}>Open</button>
                              {status === 'UNPAID' && pay.balanceDue > 0 && (
                                <button onClick={() => handleMarkAsPaid(inv)}
                                  className="font-bold px-2 py-1 rounded text-xs" style={{ backgroundColor: '#E6F7EE', color: '#0F6E56' }}>Mark Paid</button>
                              )}
                              {status === 'PARTIAL' && (
                                <button onClick={() => { setShowPaymentForm(true); setPaymentForm(f => ({ ...f, invoiceId: inv.id })); }}
                                  className="font-bold px-2 py-1 rounded text-xs" style={{ backgroundColor: '#FDF1DD', color: '#854F0B' }}>Record Payment</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ── Tab 3: History ── */}
        {/* ── Tabs: Quotes and Invoices ──────────────────────────────
            One renderer, two tabs. The columns, the sort and the row treatment
            are exactly what the combined History table had — only the contents
            of the list differ, so a shared function is the honest shape and
            keeps the two from drifting apart.

            THE TOTALS BAR IS NOT HERE. It moved above the tab strip: Total
            Quoted, Total Invoiced and Total Paid span quotes, invoices AND
            payments, so it belongs to the customer rather than to any one tab.
            Duplicating it into both would have been two things to keep true;
            leaving it in one would have hidden it from the other. */}
        {(activeTab === 'quotes' || activeTab === 'invoices') && (() => {
          const isQuotes = activeTab === 'quotes';
          const items = isQuotes ? quoteItems : invoiceItems;
          const empty = isQuotes ? 'No quotes for this customer.' : 'No invoices for this customer.';
          // The ordered ids of THIS list, handed to the document page so it can
          // step through them. A snapshot of what was on screen — see the note
          // in ViewDocument about what happens if one is deleted meanwhile.
          const ids = items.map(i => i.id);
          return (
            <div className="rounded-xl p-5" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5' }}>
              <h3 className="font-bold text-lg mb-4" style={{ color: '#03045E' }}>
                {isQuotes ? 'Quotes' : 'Invoices'}
              </h3>
              {items.length === 0 ? (
                <p className="text-sm" style={{ color: '#6B7C93' }}>{empty}</p>
              ) : (
                <table className="w-full text-sm">
                  <thead style={{ backgroundColor: '#E6F1FB' }}>
                    <tr style={{ color: '#03045E' }}>
                      <th className="px-3 py-2 text-left">Type</th>
                      <th className="px-3 py-2 text-left">Doc #</th>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => {
                      const status = item._type === 'invoice'
                        ? getStatus(item)
                        : (item.convertedToInvoice ? 'Converted' : item.isDraft ? 'Draft' : 'Active');
                      return (
                        <tr key={`${item._type}-${item.id}`}
                          onClick={() => openFromList(item, ids, idx)}
                          className="cursor-pointer transition"
                          style={{ backgroundColor: idx % 2 === 0 ? '#FFFFFF' : '#CDE7F5' }}
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#E6F1FB'}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#FFFFFF' : '#CDE7F5'}>
                          <td className="px-3 py-2">
                            <span className="px-2 py-0.5 rounded text-xs font-bold" style={item._type === 'invoice' ? { backgroundColor: '#E6F7EE', color: '#0F6E56' } : { backgroundColor: '#E6F1FB', color: '#0077B6' }}>
                              {item._type === 'invoice' ? '🧾 Invoice' : '📄 Quote'}
                            </span>
                          </td>
                          <td className="px-3 py-2 font-mono font-bold text-xs" style={{ color: '#0077B6' }}>{item.docNumber}</td>
                          <td className="px-3 py-2 text-xs" style={{ color: '#6B7C93' }}>{fmtDate(item.createdAt)}</td>
                          <td className="px-3 py-2 text-right font-bold" style={{ color: '#03045E' }}>{fmt(item.grandTotal)}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={statusBadgeClass} style={statusBadgeStyle(item._type === 'invoice' ? getStatus(item) : 'PAID')}>
                              {status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          );
        })()}

        {/* ── Tab 4: Drawings ── */}
        {/* Same card, heading, table and row treatment as the document
            history tab; only the columns differ. */}
        {activeTab === 'drawings' && (
          <div className="rounded-xl p-5" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5' }}>
            <h3 className="font-bold text-lg mb-4" style={{ color: '#03045E' }}>Shop Drawings</h3>
            {drawings.length === 0 ? (
              <p className="text-sm" style={{ color: '#6B7C93' }}>No shop drawings for this customer.</p>
            ) : (
              <table className="w-full text-sm">
                <thead style={{ backgroundColor: '#E6F1FB' }}>
                  <tr style={{ color: '#03045E' }}>
                    <th className="px-3 py-2 text-left">Drawing #</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-left">Size</th>
                    <th className="px-3 py-2 text-left">Drawn By</th>
                    <th className="px-3 py-2 text-left">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {drawings.map((d, idx) => (
                    <tr key={d.id}
                      onClick={() => navigate('/shop-drawings', { state: { drawingId: d.id } })}
                      className="cursor-pointer transition"
                      style={{ backgroundColor: idx % 2 === 0 ? '#FFFFFF' : '#CDE7F5' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#E6F1FB'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#FFFFFF' : '#CDE7F5'}>
                      <td className="px-3 py-2 font-mono font-bold text-xs" style={{ color: '#0077B6' }}>{d.drawingNumber}</td>
                      <td className="px-3 py-2" style={{ color: '#03045E' }}>{d.type}</td>
                      <td className="px-3 py-2" style={{ color: '#03045E' }}>{d.openingIn}&quot; x {d.heightIn}&quot;</td>
                      <td className="px-3 py-2 text-xs" style={{ color: '#6B7C93' }}>{d.drawnBy || '—'}</td>
                      <td className="px-3 py-2 text-xs" style={{ color: '#6B7C93' }}>{fmtTsDate(d.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── Tab 5: Payments ── */}
        {activeTab === 'payments' && customer.isAccountCustomer && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-bold text-lg" style={{ color: '#03045E' }}>Payment Records</h3>
              <button onClick={() => setShowPaymentForm(true)}
                className="font-bold px-4 py-2 rounded-lg text-sm" style={{ backgroundColor: '#0F6E56', color: '#FFFFFF' }}>
                + Record Manual Payment
              </button>
            </div>

            {/* Payment form */}
            {showPaymentForm && (
              <div className="rounded-xl p-5" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5' }}>
                <h4 className="font-bold mb-4" style={{ color: '#03045E' }}>Record Payment</h4>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block font-semibold text-sm mb-1" style={{ color: '#03045E' }}>Amount ($) *</label>
                    <input type="number" step="0.01" value={paymentForm.amount}
                      onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))}
                      className="w-full rounded-lg px-3 py-2 focus:outline-none" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                      onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'} placeholder="0.00" />
                  </div>
                  <div>
                    <label className="block font-semibold text-sm mb-1" style={{ color: '#03045E' }}>Date</label>
                    <input type="date" value={paymentForm.date}
                      onChange={e => setPaymentForm(f => ({ ...f, date: e.target.value }))}
                      className="w-full rounded-lg px-3 py-2 focus:outline-none" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                      onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'} />
                  </div>
                  <div>
                    <label className="block font-semibold text-sm mb-1" style={{ color: '#03045E' }}>Invoice *</label>
                    <select value={paymentForm.invoiceId} onChange={e => setPaymentForm(f => ({ ...f, invoiceId: e.target.value }))}
                      className="w-full rounded-lg px-3 py-2 focus:outline-none" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                      onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'}>
                      <option value="">Select invoice...</option>
                      {unpaidInvoices.map(inv => (
                        <option key={inv.id} value={inv.id}>
                          {inv.docNumber} — {fmt(inv.grandTotal)} ({getStatus(inv)}, {fmt(payStatusById[inv.id].balanceDue)} owed)
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block font-semibold text-sm mb-1" style={{ color: '#03045E' }}>Notes</label>
                    <input value={paymentForm.notes} onChange={e => setPaymentForm(f => ({ ...f, notes: e.target.value }))}
                      className="w-full rounded-lg px-3 py-2 focus:outline-none" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                      onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'} placeholder="Optional" />
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={handleRecordPayment} disabled={recordingPayment}
                    className="disabled:opacity-60 font-bold px-5 py-2 rounded-lg text-sm" style={{ backgroundColor: '#0F6E56', color: '#FFFFFF' }}>
                    {recordingPayment ? 'Recording...' : 'Record Payment'}
                  </button>
                  <button onClick={() => setShowPaymentForm(false)} className="font-bold px-5 py-2 rounded-lg text-sm" style={{ backgroundColor: '#E6F1FB', color: '#6B7C93', border: '1px solid #E3ECF5' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5' }}>
              {payments.length === 0 ? (
                <p className="p-5 text-sm" style={{ color: '#6B7C93' }}>No payments recorded.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead style={{ backgroundColor: '#E6F1FB' }}>
                    <tr style={{ color: '#03045E' }}>
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-4 py-3 text-right">Amount</th>
                      <th className="px-4 py-3 text-left">Method</th>
                      <th className="px-4 py-3 text-left">Invoice #</th>
                      <th className="px-4 py-3 text-left">Notes</th>
                      <th className="px-4 py-3 text-left">Recorded By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((pmt, idx) => (
                      <tr key={pmt.id} style={{ backgroundColor: idx % 2 === 0 ? '#FFFFFF' : '#CDE7F5' }}>
                        <td className="px-4 py-3" style={{ color: '#6B7C93' }}>{fmtDate(pmt.date || pmt.createdAt)}</td>
                        <td className="px-4 py-3 text-right font-bold" style={{ color: '#0F6E56' }}>{fmt(pmt.amount)}</td>
                        <td className="px-4 py-3 text-xs" style={{ color: '#6B7C93' }}>
                          {pmt.method ? (
                            <span>
                              {pmt.method === 'Cash' ? '💵' : pmt.method === 'Credit/Debit Card' ? '💳' : pmt.method === 'Check' ? '🏦' : '🔄'}{' '}
                              {pmt.method}
                              {pmt.checkNumber && <span className="ml-1" style={{ color: '#6B7C93' }}>#{pmt.checkNumber}</span>}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3 font-mono font-bold text-xs" style={{ color: '#0077B6' }}>{pmt.invoiceNumber || '—'}</td>
                        <td className="px-4 py-3 text-xs" style={{ color: '#6B7C93' }}>{pmt.notes || '—'}</td>
                        <td className="px-4 py-3 text-xs" style={{ color: '#6B7C93' }}>{pmt.recordedBy}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

      </div>

      {/* ── Modal: Delete Customer (admin + password gated) ── */}
      {showDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(3,4,94,0.5)' }}>
          <div className="rounded-xl p-6 max-w-md w-full" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', boxShadow: '0 8px 24px rgba(3,4,94,0.15)' }}>
            <h3 className="text-xl font-bold mb-2" style={{ color: '#A32D2D' }}>⚠️ Delete Customer</h3>
            <p className="mb-4" style={{ color: '#6B7C93' }}>
              Delete customer <strong>{customer.name}</strong>? This cannot be undone.
              Deletion is blocked if the customer has any invoices, finalized quotes, or payments.
            </p>
            <p className="font-semibold mb-2" style={{ color: '#03045E' }}>Enter your password to confirm:</p>
            <input
              type="password"
              value={deletePassword}
              onChange={e => setDeletePassword(e.target.value)}
              placeholder="Your login password"
              className="w-full rounded-lg px-4 py-2 mb-2 focus:outline-none"
              style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
              onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'}
              onKeyDown={e => { if (e.key === 'Enter' && deletePassword && !deleting) confirmDelete(); }}
              autoFocus
            />
            {deleteError && <p className="text-sm mb-2" style={{ color: '#A32D2D' }}>{deleteError}</p>}
            <div className="flex gap-3 mt-4">
              <button
                onClick={confirmDelete}
                disabled={deleting || !deletePassword}
                className="disabled:opacity-50 font-bold px-6 py-2 rounded-lg flex-1 transition"
                style={{ backgroundColor: '#A32D2D', color: '#FFFFFF' }}
              >
                {deleting ? 'Deleting…' : 'Delete Permanently'}
              </button>
              <button
                onClick={closeDeleteModal}
                disabled={deleting}
                className="font-bold px-6 py-2 rounded-lg flex-1 transition"
                style={{ backgroundColor: '#E6F1FB', color: '#6B7C93' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
}

export default CustomerProfile;
